import { useContext, useEffect, useState } from "react";
import { CollectionDetailsContext } from "contexts/CollectionDetails";
import { getCurrentSessionId } from "lib/session";
import clsx from "clsx";
import { useTaskDefinition } from "hooks/useTaskDefinition";
import { captureDB } from "lib/indexed-db";
import { getTaskStepProgress } from "lib/task-progress";

export function TaskStepProgress({
  taskId,
  captureType,
  workTaskDocumentId,
}: {
  taskId: string;
  captureType: string;
  workTaskDocumentId?: string;
}) {
  const { instructions, workTask, orbTask, loading, error } = useTaskDefinition(
    { taskId, workTaskDocumentId, enabled: true }
  );
  const { contributor } = useContext(CollectionDetailsContext);
  const flow = workTask || orbTask;
  const [progress, setProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);
  const [readError, setReadError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setProgress(null);
    setReadError(false);
    if (loading || error) return;
    const readProgress = async () => {
      try {
        const [captures, details] = await Promise.all([
          captureDB.getStepCaptures(taskId),
          captureDB.getStepDetails(taskId),
        ]);
        const sessionId = await getCurrentSessionId(contributor);
        const completedSteps = sessionId
          ? await captureDB.getCompletedSteps(sessionId, taskId)
          : null;
        const next = getTaskStepProgress({
          flow,
          instructions,
          captureType,
          captures,
          details,
          completedSteps,
        });
        if (!cancelled) {
          setProgress(next);
          setReadError(false);
        }
      } catch (failure) {
        console.error(`Could not load step progress for ${taskId}:`, failure);
        if (!cancelled) setReadError(true);
      }
    };
    void readProgress();
    window.addEventListener("focus", readProgress);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", readProgress);
    };
  }, [taskId, captureType, instructions, flow, contributor, loading, error]);

  if (error || readError)
    return (
      <span className="text-xs" role="status">
        Progress unavailable
      </span>
    );
  if (!progress)
    return (
      <span className="text-xs" role="status">
        Loading progress…
      </span>
    );
  const { completed, total } = progress;
  return (
    <div
      className="relative w-12 h-12"
      aria-label={`${completed} of ${total} steps complete`}
    >
      <svg
        className="w-12 h-12 transform -rotate-90"
        viewBox="0 0 36 36"
        aria-hidden="true"
      >
        <path
          className="text-gray-200"
          stroke="currentColor"
          strokeWidth="3"
          fill="none"
          d="M18 2.5 a 15.5 15.5 0 0 1 0 31 a 15.5 15.5 0 0 1 0 -31"
        />
        <path
          className={clsx(
            completed === total ? "text-green-500" : "text-blue-500"
          )}
          stroke="currentColor"
          strokeWidth="3"
          fill="none"
          strokeDasharray={`${(completed / total) * 97.4}, 97.4`}
          d="M18 2.5 a 15.5 15.5 0 0 1 0 31 a 15.5 15.5 0 0 1 0 -31"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-semibold text-gray-700">
          {completed}/{total}
        </span>
      </div>
    </div>
  );
}
